import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export const ACTIONS = {
  login_success: ["Signed in", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  login_failed: ["Failed sign-in", "bg-rose-50 text-rose-700 border-rose-200"],
  account_locked: ["Account locked", "bg-rose-100 text-rose-800 border-rose-300"],
  logout: ["Signed out", "bg-slate-50 text-slate-700 border-slate-200"],
  auto_logout: ["Auto sign-out", "bg-slate-50 text-slate-700 border-slate-200"],
  device_requested: ["New device", "bg-amber-50 text-amber-800 border-amber-200"],
  device_approved: ["Device approved", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  device_rejected: ["Device blocked", "bg-rose-50 text-rose-700 border-rose-200"],
  user_unlocked: ["User unlocked", "bg-blue-50 text-blue-700 border-blue-200"],
  corporate_created: ["Corporate created", "bg-blue-50 text-blue-700 border-blue-200"],
  corporate_updated: ["Corporate edited", "bg-blue-50 text-blue-700 border-blue-200"],
  corporate_deleted: ["Corporate deleted", "bg-rose-50 text-rose-700 border-rose-200"],
  area_added: ["Area added", "bg-blue-50 text-blue-700 border-blue-200"],
  area_removed: ["Area removed", "bg-slate-50 text-slate-700 border-slate-200"],
};
const fmt = (iso) => new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });

export default function ActivityLog() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState("all");
  const [q, setQ] = useState("");

  useEffect(() => {
    const t = setTimeout(() => {
      setLoading(true);
      api.get("/activity-logs", { params: { action: action === "all" ? undefined : action, q: q || undefined } })
        .then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [action, q]);

  return (
    <div className="space-y-6" data-testid="activity-log">
      <div>
        <h1 className="text-3xl font-serif font-semibold">Activity Log</h1>
        <p className="text-sm text-muted-foreground mt-1">Sign-ins, sign-outs, failed attempts and corporate changes across the portal (latest 300).</p>
      </div>
      <div className="flex flex-wrap gap-3">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search user, email or details…" className="max-w-xs" data-testid="activity-search-input" />
        <Select value={action} onValueChange={setAction}>
          <SelectTrigger className="w-56" data-testid="activity-action-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All activity</SelectItem>
            {Object.entries(ACTIONS).map(([k, [l]]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="rounded-xl border border-border bg-card overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow><TableHead>When</TableHead><TableHead>User</TableHead><TableHead>Activity</TableHead><TableHead>Details</TableHead><TableHead>IP</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} className="h-32 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-primary" /></TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="h-32 text-center text-sm text-muted-foreground" data-testid="activity-empty">No activity found.</TableCell></TableRow>
            ) : rows.map((r) => {
              const [label, style] = ACTIONS[r.action] || [r.action, "bg-slate-50 text-slate-700 border-slate-200"];
              return (
                <TableRow key={r.id} data-testid={`activity-row-${r.id}`}>
                  <TableCell className="text-sm whitespace-nowrap text-muted-foreground">{fmt(r.at)}</TableCell>
                  <TableCell className="font-medium">{r.user_name || "—"}<div className="text-xs text-muted-foreground font-normal">{r.email}</div></TableCell>
                  <TableCell><span className={`text-xs px-2.5 py-1 rounded-full border font-medium whitespace-nowrap ${style}`}>{label}</span></TableCell>
                  <TableCell className="text-sm">{r.details}</TableCell>
                  <TableCell className="text-xs font-mono text-muted-foreground">{r.ip}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
